import React, { createRef } from 'react';
import classNameBind from 'classnames/bind';
import _isString from 'lodash/isString';
import _isArray from 'lodash/isArray';
import { Tag } from '../Tag';
import Tooltip from '../Tooltip';
import getClipboardData from '../../utils/methods/getClipboardData';
import styles from './TagsInput.module.css';
import { KEYS } from '../../utils/methods/keyboardKeys';

const bindClasses = classNameBind.bind(styles);

const parseValue = value => (
  value.map(item => (
    _isString(item)
      ? { value: item }
      : item
  ))
);

export default class TagsInput extends React.Component {
  // eslint-disable-next-line react/sort-comp
  static defaultSeparators = ['\\n', '\\r']

  input = createRef();

  element = createRef();

  static defaultProps = {
    disabled: false,
    onChange: () => {},
    onBlur: () => {},
    error: [],
    splitSeparators: [],
    placeholder: '',
    autoFocus: false,
    editable: true,
    touched: [],
    canAddMore: true,
    appearance: 'default',
  }

  constructor(props) {
    super(props);

    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      selectedToRemoveIndex: null,
    };
  }

  getSplitSeparators() {
    const { splitSeparators } = this.props;

    return splitSeparators.concat(TagsInput.defaultSeparators);
  }

  getIndexFromName = name => (
    name.match(/\[(.*)\]/)[1]
  );

  focus = () => {
    this.input.current.focus();
  }

  select = () => {
    this.input.current.select();
  }

  clear = () => {
    this.input.current.value = '';
  }

  handleTagChange = (event) => {
    const { onChange, name, value } = this.props;

    const index = this.getIndexFromName(event.target.name);

    if (event.target.value === '') {
      return this.handleTagRemoval(index);
    }

    // Array is passed by reference so to avoid changing
    // prop from inside of component ( fake state )
    // we need to clone value
    const currentValue = [...value];
    currentValue[index] = event.target.value;

    return onChange({
      target: {
        name,
        value: currentValue,
      },
    });
  }

  handleTagKeyDown = (e) => {
    // Do not allow to use this signs durning value edition
    if (e.key === KEYS.ENTER) {
      e.preventDefault();
    }
  }

  handleKeyPress = (e) => {
    const { onPressEnter, canAddMore } = this.props;

    if (e.key === KEYS.ENTER) {
      if (onPressEnter) {
        onPressEnter(e);
      } else {
        if (this.input.current.value === '' || !canAddMore) {
          return;
        }
        e.preventDefault();
        this.pushRecordToValue(e);
      }
    }
  }

  handleFocus = (event) => {
    const { onFocus } = this.props;

    // When input is inside form press enter works as submit
    // This is workaround to block form submit when component is focused
    if (event.target.form) {
      event.target.form.addEventListener('keypress', this.handleKeyPress);
    }

    if (onFocus) {
      onFocus();
    }
  }

  handleBlur = (event) => {
    // Need to clear listener, so other controls could still work
    if (event.target.form) {
      event.target.form.removeEventListener('keypress', this.handleKeyPress);
    }
  }

  handleComponentBlur = (event) => {
    const {
      onBlur,
      name,
      value,
      canAddMore,
    } = this.props;

    if (onBlur && canAddMore && !this.element.current.contains(event.relatedTarget)) {
      // Array is passed by reference so to avoid changing
      // prop from inside of component ( fake state )
      // we need to clone value
      let currentValue = value.length > 0 ? [...value] : [];
      if (this.input.current.value !== '') {
        // Update value about not submitted data
        currentValue = [...value, this.input.current.value];
      }

      onBlur({
        target: {
          name,
          value: currentValue,
        },
      });

      this.clear();
      this.setState(this.getInitialState());
    }
  }

  handleWrapperClick = (event) => {
    if (event.target === event.currentTarget) {
      this.input.current.focus();
    }
  }

  handleTagRemoval = (index) => {
    const { selectedToRemoveIndex } = this.state;
    const { name, onChange, value } = this.props;
    const currentValue = [...value];

    currentValue.splice(index, 1);

    if (selectedToRemoveIndex !== null) {
      const defaultValue = value.length > 0 ? 0 : null;
      this.setState({
        selectedToRemoveIndex: index !== 0 ? index - 1 : defaultValue,
      });
    }

    onChange({
      target: {
        name,
        // Array is passed by reference so to avoid changing
        // prop from inside of component ( fake state )
        // we need to clone value
        value: currentValue,
      },
    });
    this.input.current.focus();
  }

  handlePaste = (event) => {
    const {
      onChange,
      value,
      name,
      maxLength,
      canAddMore,
    } = this.props;

    event.preventDefault();

    if (!canAddMore || (maxLength && value.length >= maxLength)) {
      return;
    }

    // Get records from clipboard add them to rest and clear duplicates and empty ones
    const newValue = value.concat(this.splitTags(getClipboardData(event)))
      .filter((v, i, a) => a.indexOf(v) === i)
      .filter(v => (v.trim ? v.trim() !== '' : true)).slice(0, maxLength);

    onChange({
      target: {
        name,
        value: newValue,
      },
    });
  }

  getTagColor = (tag, error, index) => {
    if (tag.disabled) {
      return 'white';
    }

    if (error && error[index]) {
      return 'red';
    }

    return 'gray';
  }

  handleKeyDown = (e) => {
    const { onKeyDown, onPressEnter, canAddMore } = this.props;

    if (onKeyDown) {
      onKeyDown(e);
    }

    switch (e.key) {
      case KEYS.TAB:
        // When press tab on empty input should jump to another control
        if (this.input.current.value === '' || !canAddMore) {
          return;
        }
        e.preventDefault();
        this.pushRecordToValue(e);
        break;
      case KEYS.ENTER:
        if (e.target.form) {
          return;
        }

        if (onPressEnter) {
          onPressEnter(e);
        }

        if (this.input.current.value !== '' && canAddMore) {
          e.preventDefault();
          this.pushRecordToValue(e);
        }
        break;
      case KEYS.LEFT:
        this.handleArrowLeftKey(e);
        break;
      case KEYS.RIGHT:
        this.handleArrowRightKey(e);
        break;
      case KEYS.BACKSPACE:
        this.handleBackspaceKey(e);
        break;
      default:
        // Handle custom separators provided with prop
        if (canAddMore && this.getSplitSeparators().includes(e.key)) {
          e.preventDefault();
          this.pushRecordToValue(e);
        }
        // Need to remove selection when type something in input
        this.setState({
          selectedToRemoveIndex: null,
        });
        break;
    }
  }

  handleArrowLeftKey(e) {
    const { selectedToRemoveIndex } = this.state;

    if (selectedToRemoveIndex !== null) {
      e.preventDefault();
      this.setState({
        // Need to make to not exceed range of array
        selectedToRemoveIndex: selectedToRemoveIndex > 0
          ? selectedToRemoveIndex - 1
          : selectedToRemoveIndex,
      });
    }
  }

  handleArrowRightKey(e) {
    const { value } = this.props;
    const { selectedToRemoveIndex } = this.state;

    if (selectedToRemoveIndex !== null) {
      e.preventDefault();
      this.setState({
        // Need to make to not exceed range of array
        selectedToRemoveIndex: selectedToRemoveIndex < value.length - 1
          ? selectedToRemoveIndex + 1
          : selectedToRemoveIndex,
      });
    }
  }

  handleBackspaceKey(e) {
    const { value } = this.props;
    const { selectedToRemoveIndex } = this.state;

    // Needed to allow remove letters when input is not empty
    if (this.input.current.value === '') {
      e.preventDefault();
      if (selectedToRemoveIndex !== null && value.length > 0) {
        if (value[selectedToRemoveIndex].disabled) {
          return;
        }
        // this.setState({
        //   // Need to clear selection when remove last element
        //   // so outline could be display on whole component again
        //   selectedToRemoveIndex: value.length > 1 ? selectedToRemoveIndex - 1 : null,
        // });
        this.handleTagRemoval(selectedToRemoveIndex);
      } else {
        // Select last element on first backspace
        this.setState({
          selectedToRemoveIndex: value.length - 1,
        });
      }
    }
  }

  pushRecordToValue(e) {
    const {
      onChange,
      value,
      maxLength,
      name,
    } = this.props;

    if (maxLength && value.length >= maxLength) return;
    // Avoid creating tag with the same value and value with only white signs
    if (value.every(v => v !== e.target.value) && e.target.value.trim() !== '') {
      onChange({
        target: {
          name,
          // Array is passed by reference so to avoid changing
          // prop from inside of component ( fake state )
          // we need to clone value
          value: [...value, e.target.value],
        },
      });
    }

    this.clear();
    // Each time we add another item in value we increase index in input
    this.input.current.focus();
  }

  splitTags(data) {
    return data.split(new RegExp(this.getSplitSeparators().join('|')));
  }

  render() {
    const {
      id,
      value,
      name,
      maxLength,
      error,
      disabled,
      editable,
      placeholder,
      autoFocus,
      touched,
      onNextChange,
      appearance,
    } = this.props;
    const {
      selectedToRemoveIndex,
    } = this.state;
    const parsedValue = parseValue(value);
    const hasError = _isArray(error) ? error.length > 0 : error !== undefined;

    return (
      <div
        className={bindClasses('wrapper', {
          invalid: hasError && touched,
          required: parsedValue.length === 0,
          noOutline: selectedToRemoveIndex !== null,
        }, `appearance-${appearance}`)}
        onClick={this.handleWrapperClick}
        onBlur={this.handleComponentBlur}
        ref={this.element}
        role="button"
        tabIndex="-1"
        data-ppuc-id="TagsInput"
      >
        {parsedValue.map((tag, index) => (
          <Tag
            id={index}
            color={this.getTagColor(tag, error, index)}
            onRemove={this.handleTagRemoval}
            isFocused={selectedToRemoveIndex === index}
            /* eslint-disable-next-line react/no-array-index-key */
            key={index}
            isRemovable={!disabled && !tag.disabled}
            tabIndex={-1}
          >
            <Tooltip {...tag.tooltip} content={error[index] || (tag.tooltip && tag.tooltip.content)}>
              <div className={bindClasses('tag-value-wrapper')}>
                <textarea
                  className={bindClasses('textarea', { editable })}
                  name={`${name}[${index}]`}
                  value={tag.value}
                  onChange={this.handleTagChange}
                  onKeyDown={this.handleTagKeyDown}
                  disabled={!editable || disabled || tag.disabled}
                  tabIndex={-1}
                />
                <pre className={bindClasses('textarea-resize')}>{tag.value}</pre>
              </div>
            </Tooltip>
          </Tag>
        ))}
        <input
          id={id}
          type="text"
          // Input change name every time we change value length
          name={`${name}[${parsedValue.length}]`}
          className={bindClasses('input')}
          defaultValue=""
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onKeyDown={this.handleKeyDown}
          onPaste={this.handlePaste}
          onChange={onNextChange}
          value={value[parsedValue.length]}
          ref={this.input}
          placeholder={placeholder}
          style={{ display: disabled ? 'none' : 'inline' }}
          disabled={disabled}
          // Instead of using disabled when add last item in provided range
          // readOnly prop is setup to not loose focus from input
          readOnly={maxLength && parsedValue.length >= maxLength}
          autoComplete="off"
          autoFocus={autoFocus}
          data-ppuc-id="TagsInput.input"
        />
      </div>
    );
  }
}
